<template>
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
        <!-- 顶部导航 -->
        <header class="bg-white dark:bg-gray-800 shadow-md py-4 px-6 sticky top-0 z-10 transition-colors duration-300">
            <div class="container mx-auto flex justify-between items-center">
                <div class="flex items-center space-x-4">
                    <button @click="goBack" class="flex items-center text-reader-text hover:text-primary">
                        <i class="fa fa-arrow-left mr-2"></i>
                        <span class="text-sm">返回</span>
                    </button>
                    <h1 class="text-xl font-bold text-gray-800 dark:text-white">我的小说库</h1>
                </div>

                <div class="flex items-center space-x-3">
                    <!-- 搜索框 -->
                    <div class="relative">
                        <input
                            type="text"
                            placeholder="搜索小说..."
                            class="pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary/70 transition-all"
                        >
                        <i class="fa fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>

                    <!-- 主题切换 -->
                    <button
                        @click="toggleTheme"
                        class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="切换主题"
                    >
                        <i :class="isDarkMode ? 'fa fa-sun-o' : 'fa fa-moon-o'"></i>
                    </button>
                </div>
            </div>
        </header>

        <!-- 主内容区 -->
        <main class="container mx-auto p-4 md:p-8">
            <!-- 小说列表 -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                <!-- 小说卡片 -->
                <div
                    v-for="novel in novels"
                    :key="novel.id"
                    class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group cursor-pointer"
                    @click="selectNovel(novel.id)"
                >
                    <!-- 小说封面 -->
                    <div class="relative h-64 overflow-hidden">
                        <img
                            :src="novel.coverImage"
                            alt="小说封面"
                            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        >
                        <!-- 阅读进度覆盖层 -->
                        <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                            <div class="w-full">
                                <div class="w-full bg-gray-700 rounded-full h-2">
                                    <div class="bg-primary h-2 rounded-full" :style="{ width: `${novel.progress}%` }"></div>
                                </div>
                                <p class="text-white text-sm mt-1">已阅读: {{ novel.progress }}%</p>
                            </div>
                        </div>
                    </div>

                    <!-- 小说信息 -->
                    <div class="p-4">
                        <h3 class="font-bold text-lg text-gray-800 dark:text-white truncate group-hover:text-primary transition-colors">
                            {{ novel.title }}
                        </h3>
                        <p class="text-gray-600 dark:text-gray-400 text-sm mt-1 line-clamp-2">
                            {{ novel.description }}
                        </p>
                        <div class="flex justify-between items-center mt-3">
              <span class="text-xs text-gray-500 dark:text-gray-500">
                {{ novel.chapterCount }} 章 · {{ novel.wordCount }} 字
              </span>
                            <span class="text-xs text-gray-500 dark:text-gray-500">
                更新于 {{ novel.updatedAt}}
              </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 加载更多 -->
            <div class="mt-12 text-center">
                <button
                    class="px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                    @click="loadMore"
                >
                    加载更多 <i class="fa fa-angle-down ml-1"></i>
                </button>
            </div>
        </main>

        <!-- 页脚 -->
        <footer class="bg-white dark:bg-gray-800 py-4 px-6 mt-12 transition-colors duration-300">
            <div class="container mx-auto text-center text-sm text-gray-600 dark:text-gray-400">
                <p>版权与备案信息：&copy; 2026 LiAng. 保留所有权利. | 渝ICP备2026005016号-1</p>
            </div>
        </footer>
    </div>
</template>

<script setup>
import {ref, onMounted, watch, onUnmounted} from 'vue';
import { useRouter } from 'vue-router';
import {novelSelectorWebSocket} from "@/services/NovelSelectorWebSocket";

import {useWebSocket} from "@/services/HomeWebSocketService";
import {draftVisionStore} from "@/store/listDraftVisible";
// 状态管理
const visionStore = draftVisionStore()
const router = useRouter();
const isDarkMode = ref(false);
const { connected, novels, loading, error, fetchNovels } = novelSelectorWebSocket();
const loadData = async () => {
    fetchNovels();
};

// 主题切换
const toggleTheme = () => {
    isDarkMode.value = !isDarkMode.value;
    document.documentElement.classList.toggle('dark', isDarkMode.value);
};

// 选择小说
const selectNovel = (novelId) => {
    visionStore.setCurrentVision(false,true,false, '章节目录', 'list')
    // 导航到小说详情页
    router.push({ name: 'NovelDetail', params: { id: novelId } });
    // 在目标路由的组件中，可以通过 $route.params.id 访问这个值。
};

// 加载更多小说
const loadMore = () => {
    // 模拟加载更多数据
    alert('加载更多小说...');
};

// 初始化主题
onMounted(() => {
    // 检查系统主题偏好
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    isDarkMode.value = prefersDark;
    document.documentElement.classList.toggle('dark', prefersDark);
    loadData()
});
onUnmounted(() => {

})
// 导航函数
const goBack = () => {
    router.go(-1)
}
</script>

<style scoped>
/* 自定义滚动条 */
::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

::-webkit-scrollbar-track {
    background: transparent;
}

::-webkit-scrollbar-thumb {
    background: #c5c5c5;
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

.dark ::-webkit-scrollbar-thumb {
    background: #555;
}

.dark ::-webkit-scrollbar-thumb:hover {
    background: #666;
}
</style>
